import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';

const FieldError = ({ id, children }) => children ? (
  <p id={id} role="alert" className="text-xs text-destructive" data-testid={id}>{children}</p>
) : null;

export const ProfileQuestionnaireFields = ({ form, errors, setField, disabled = false, testIdPrefix = 'signup' }) => (
  <div className="space-y-5">
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor="first-name">First name</Label>
        <Input id="first-name" autoComplete="given-name" value={form.firstName}
          onChange={event => setField('firstName', event.target.value)} disabled={disabled}
          aria-invalid={Boolean(errors.firstName)} aria-describedby="first-name-error"
          data-testid={`${testIdPrefix}-first-name-input`} />
        <FieldError id="first-name-error">{errors.firstName}</FieldError>
      </div>
      <div className="space-y-2">
        <Label htmlFor="last-name">Last name</Label>
        <Input id="last-name" autoComplete="family-name" value={form.lastName}
          onChange={event => setField('lastName', event.target.value)} disabled={disabled}
          aria-invalid={Boolean(errors.lastName)} aria-describedby="last-name-error"
          data-testid={`${testIdPrefix}-last-name-input`} />
        <FieldError id="last-name-error">{errors.lastName}</FieldError>
      </div>
    </div>

    <div className="space-y-2">
      <Label htmlFor="contact-phone">Contact phone</Label>
      <PhoneInput id="contact-phone" international countryCallingCodeEditable={false}
        value={form.phone || undefined} onChange={value => setField('phone', value || '')}
        onCountryChange={country => setField('phoneCountry', country || '')}
        disabled={disabled} autoComplete="tel" aria-invalid={Boolean(errors.phone)}
        aria-describedby="contact-phone-help contact-phone-error"
        className="aha-phone-input" data-testid={`${testIdPrefix}-phone-input`} />
      <p id="contact-phone-help" className="text-xs text-muted-foreground">
        Contact information only. This number is not verified by SMS.
      </p>
      <FieldError id="contact-phone-error">{errors.phone}</FieldError>
    </div>

    <fieldset className="space-y-3" aria-describedby="account-type-help account-type-error">
      <legend className="text-sm font-medium">Account type</legend>
      <p id="account-type-help" className="text-xs text-muted-foreground">
        Descriptive profile information only; it does not grant permissions or paid access.
      </p>
      <RadioGroup value={form.accountType} onValueChange={value => {
        setField('accountType', value);
        if (value !== 'commercial') setField('companyName', '');
      }}
        className="grid gap-3 sm:grid-cols-2" disabled={disabled} data-testid={`${testIdPrefix}-account-type-group`}>
        {[
          ['commercial', 'Commercial'],
          ['personal', 'Personal'],
        ].map(([value, label]) => (
          <Label key={value} htmlFor={`account-${value}`}
            className="flex cursor-pointer items-center gap-3 rounded-md border bg-background p-4 hover:border-primary">
            <RadioGroupItem id={`account-${value}`} value={value}
              data-testid={`${testIdPrefix}-account-${value}-radio`} />
            <span>{label}</span>
          </Label>
        ))}
      </RadioGroup>
      <FieldError id="account-type-error">{errors.accountType}</FieldError>
    </fieldset>

    <div className="space-y-2">
      <Label htmlFor="company-name">
        Company name
        {form.accountType !== 'commercial' && <span className="text-muted-foreground"> (Commercial accounts only)</span>}
      </Label>
      <Input id="company-name" autoComplete="organization" value={form.companyName}
        onChange={event => setField('companyName', event.target.value)}
        disabled={disabled || form.accountType !== 'commercial'}
        required={form.accountType === 'commercial'}
        aria-invalid={Boolean(errors.companyName)} aria-describedby="company-name-help company-name-error"
        data-testid={`${testIdPrefix}-company-input`} />
      <p id="company-name-help" className="text-xs text-muted-foreground">
        {form.accountType === 'commercial'
          ? 'Required for Commercial accounts.'
          : form.accountType === 'personal' ? 'Not stored for Personal accounts.' : 'Select an account type to continue.'}
      </p>
      <FieldError id="company-name-error">{errors.companyName}</FieldError>
    </div>

    <div className="rounded-md border bg-muted/35 p-4">
      <div className="flex items-start gap-3">
        <Checkbox id="legal-acceptance" checked={form.legalAccepted}
          onCheckedChange={checked => setField('legalAccepted', checked === true)} disabled={disabled}
          aria-describedby="legal-acceptance-error" data-testid={`${testIdPrefix}-legal-acceptance-checkbox`} />
        <Label htmlFor="legal-acceptance" className="font-normal leading-5">
          I have read and agree to the{' '}
          <a href="/legal#terms" target="_blank" rel="noreferrer" className="font-medium text-primary underline"
            data-testid={`${testIdPrefix}-terms-link`}>Terms of Service</a>{' '}
          and acknowledge the{' '}
          <a href="/legal#privacy" target="_blank" rel="noreferrer" className="font-medium text-primary underline"
            data-testid={`${testIdPrefix}-privacy-link`}>Privacy Notice</a>.
        </Label>
      </div>
      <FieldError id="legal-acceptance-error">{errors.legalAccepted}</FieldError>
    </div>
  </div>
);